import React, { Component } from 'react';
import _throttle from 'lodash/throttle';
import _find from 'lodash/find';
import { MAPBOX_MAP_STYLE_KEY_TRIPX, MAPBOX_MAP_STYLE_KEY_SATELITE } from './constants';
import MapComponent from './MapComponent';
import capitalize from 'utils/string/capitalize';
import { browserSupportsWebGL } from 'utils/webgl/webglUtils';

const mapboxgl = process.browser ? require('mapbox-gl') : {};

const TRIPX_CUSTOM = 'https://website-pages.s3.eu-west-1.amazonaws.com/map_style_v2.json';
const SATELLITE = 'mapbox://styles/mapbox/satellite-v9';

class Map extends Component {
  constructor(props) {
    super(props);

    if (!process.browser) {
      return;
    }

    this.webGLEnabled = browserSupportsWebGL();

    if (this.webGLEnabled) {
      this.state = {
        selectedStyleKey: MAPBOX_MAP_STYLE_KEY_TRIPX,
        onlyHotelsOnMap: true,
        mapWasMoved: false,
      };

      this.handleOnMapLoad = this.handleOnMapLoad.bind(this);
      this.handleOnMapMove = this.handleOnMapMove.bind(this);
      this.handleOnMapMoveend = this.handleOnMapMoveend.bind(this);
      this.handleOnMapZoom = this.handleOnMapZoom.bind(this);
      this.handleOnMapResize = this.handleOnMapResize.bind(this);
      this.handleOnMapIdle = this.handleOnMapIdle.bind(this);
      this.handleOnMapWheel = this.handleOnMapWheel.bind(this);

      this.handleOnClickOnlyHotelsOnMapCheckbox = this.handleOnClickOnlyHotelsOnMapCheckbox.bind(this);
      this.handleOnClickRedoSearchHere = this.handleOnClickRedoSearchHere.bind(this);

      this.mapStyles = {
        [MAPBOX_MAP_STYLE_KEY_TRIPX]: {
          style: TRIPX_CUSTOM,
        },
        [MAPBOX_MAP_STYLE_KEY_SATELITE]: {
          style: SATELLITE,
        },
      };
    }
  }

  // Lifecycle Events --- Start //
  componentDidMount() {
    if (!process.browser || !this.webGLEnabled) {
      return;
    }

    const { config, mapContainerRef } = this.props;
    const { mapOptions, mapEvents, controls } = config;
    // const { selectedStyleKey } = this.state;

    const _style =
      this.state && this.state.selectedStyleKey ? this.mapStyles[MAPBOX_MAP_STYLE_KEY_TRIPX].style : TRIPX_CUSTOM;

    this.map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: _style,
      ...mapOptions,
    });

    this.map.on('load', this.handleOnMapLoad);

    if (mapEvents) {
      mapEvents.forEach(({ type, evented = 'on', throttleDelay }) => {
        throttleDelay
          ? this.map[evented](type, _throttle(this[`handleOnMap${capitalize(type)}`], throttleDelay))
          : this.map[evented](type, this[`handleOnMap${capitalize(type)}`]);
      });
    }

    if (controls) {
      controls.forEach(({ key, position, options }) => {
        this.map.addControl(new mapboxgl[key](options), position);
      });
    }

    if (mapContainerRef && mapContainerRef.current) {
      mapContainerRef.current.addEventListener('wheel', this.handleOnMapWheel);
    }
  }

  componentWillUnmount() {
    const { mapContainerRef } = this.props;
    this.map && this.map.remove && this.map.remove();

    if (mapContainerRef && mapContainerRef.current) {
      mapContainerRef && mapContainerRef.current.removeEventListener('wheel', this.handleOnMapWheel);
    }
  }
  // Lifecycle Events --- End //

  // Map Events --- Start //
  handleOnMapLoad() {
    this.props.onMapLoad && this.props.onMapLoad({ map: this.map });
  }

  handleOnMapMove(event) {
    const mapEvent = _find(this.props.config.mapEvents, ['type', 'move']);
    mapEvent && mapEvent.listener(event);
  }

  handleOnMapMoveend(event, data = { forceReload: false }) {
    const { onlyHotelsOnMap } = this.state;
    const mapEvent = _find(this.props.config.mapEvents, ['type', 'moveend']);

    mapEvent && mapEvent.listener(event, { onlyHotelsOnMap, forceReload: data.forceReload });
    !onlyHotelsOnMap && !data.forceReload && this.setState({ mapWasMoved: true });
  }

  handleOnMapZoom(event) {
    const mapEvent = _find(this.props.config.mapEvents, ['type', 'zoom']);
    mapEvent && mapEvent.listener(event);
  }

  // Add 'wheel' event listener to container to prevent default scroll,
  // as map's onWheel doesn't allow preventDefault().
  handleOnMapWheel(event) {
    const { mapContainerRef } = this.props;
    if (!this.map) {
      return;
    }

    event.preventDefault();

    // Get the mouse position relative to the map container
    const mouseX = event.clientX - mapContainerRef.current.getBoundingClientRect().left;
    const mouseY = event.clientY - mapContainerRef.current.getBoundingClientRect().top;

    // Calculate the offset to zoom towards the mouse position
    const offset = [
      mouseX - mapContainerRef.current.clientWidth / 2,
      mouseY - mapContainerRef.current.clientHeight / 2,
    ];

    const easing = (t) => t * (2 - t);

    if (event.deltaY > 0) {
      this.map.zoomOut({
        duration: 500,
        offset,
        easing,
      });
    } else {
      this.map.zoomIn({
        duration: 500,
        offset,
        easing,
      });
    }
  }

  handleOnMapResize(event) {
    const mapEvent = _find(this.props.config.mapEvents, ['type', 'resize']);
    mapEvent && mapEvent.listener(event);
  }

  handleOnMapIdle(event) {
    const mapEvent = _find(this.props.config.mapEvents, ['type', 'idle']);
    mapEvent && mapEvent.listener(event);
  }
  // Map Events --- End //

  handleOnClickOnlyHotelsOnMapCheckbox() {
    this.setState(({ onlyHotelsOnMap }) => ({
      onlyHotelsOnMap: !onlyHotelsOnMap,
    }));
  }

  handleOnClickRedoSearchHere() {
    this.handleOnMapMoveend(undefined, { forceReload: true });
    this.setState({ mapWasMoved: false });
  }

  render() {
    const { mapWasMoved, onlyHotelsOnMap } = this.state ? this.state : {};
    const { children, config } = this.props;
    const { showSearchTriggerToggle, styles } = config;

    return (
      <MapComponent
        children={children}
        webGLEnabled={this.webGLEnabled}
        showSearchTriggerToggle={showSearchTriggerToggle}
        styles={styles}
        mapWasMoved={mapWasMoved}
        onlyHotelsOnMap={onlyHotelsOnMap}
        onClickRedoSearchHere={this.handleOnClickRedoSearchHere}
        onClickOnlyHotelsOnMapCheckbox={this.handleOnClickOnlyHotelsOnMapCheckbox}
      />
    );
  }
}

export default Map;
